import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion, useReducedMotion } from 'framer-motion';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControl,
    IconButton,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContactDialog from '../components/ContactDialog';
import LiveRankingsGate from '../components/LiveRankingsGate';
import { useLiveRankingsAccessGate } from '../hooks/useLiveRankingsAccessGate';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { formatNumber, getS3Url } from './rankings/RankingPageLayout';
import {
    HeaderBrandTitle,
    HeaderTitle,
    LIVE_RANKINGS_PAGE_MAX_WIDTH,
    LIVE_ROW_BG,
    liveRankingsPageContainerSx,
    liveRankingsPageOuterSx,
    PageContainer,
    WeekBadge,
} from './LiveRankings';
import {
    getPublicExplorerChannelVideos,
    getPublicExplorerChannels,
    getPublicExplorerDefaultSortForMode,
    getPublicExplorerProgramVideos,
    getPublicExplorerPrograms,
    getYouTubeUrl,
    isPublicExplorerSortAllowedForMode,
    type PublicExplorerChannelRow,
    type PublicExplorerProgramRow,
    type PublicExplorerProgramsParams,
    type PublicExplorerRankingMode,
    type PublicExplorerSortField,
} from '../api/explorerPublic';
import { explorerDarkTheme, SURFACE_MUTED } from '../theme/explorerDarkTheme';

const POPUP_DELAY_MS = 10_000;

interface ExplorerRankingsPopupProps {
    programCount: number;
    channelCount: number;
}

const ExplorerRankingsPopup: React.FC<ExplorerRankingsPopupProps> = ({
    programCount,
    channelCount,
}) => {
    const [visible, setVisible] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), POPUP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const handleDismiss = () => {
        setVisible(false);
    };

    const handleOpenContact = () => {
        analytics.track(AnalyticsEvent.CLICK_REQUEST_EXPLORER, {
            source: 'explorer_rankings_popup',
            programCount,
            channelCount,
        });
        setContactOpen(true);
    };

    const handleContactClose = () => {
        setContactOpen(false);
        setVisible(false);
    };

    if (!visible && !contactOpen) return null;

    const showLeadCard = visible && !contactOpen;
    const hasCounts = programCount > 0 && channelCount > 0;

    return (
        <>
            {showLeadCard && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '16px',
                        right: '16px',
                        left: { xs: '16px', sm: 'auto' },
                        width: { xs: 'auto', sm: '300px' },
                        zIndex: 1400,
                        backgroundColor: LIVE_ROW_BG,
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            pt: 1.5,
                            pb: 0.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"DM Serif Display", serif',
                                fontWeight: 800,
                                fontSize: '1rem',
                                color: 'white',
                                lineHeight: 1,
                            }}
                        >
                            Podscope
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleDismiss}
                            aria-label="Cerrar"
                            sx={{
                                color: 'rgba(255,255,255,0.4)',
                                p: '4px',
                                '&:hover': { color: 'rgba(255,255,255,0.8)' },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                px: 2,
                                pt: 0.5,
                                fontSize: '0.875rem',
                                color: 'rgba(255,255,255,0.85)',
                                fontFamily: '"DM Sans", sans-serif',
                                lineHeight: 1.5,
                            }}
                        >
                            {hasCounts ? (
                                <>
                                    {'Hay '}
                                    <strong style={{ color: 'white' }}>
                                        {programCount.toLocaleString('es-CL')}
                                    </strong>
                                    {' programas de '}
                                    <strong style={{ color: 'white' }}>
                                        {channelCount.toLocaleString('es-CL')}
                                    </strong>
                                    {' canales en el ranking de esta semana.'}
                                </>
                            ) : (
                                'Explora el ranking de programas y canales con métricas a 7 días de estreno.'
                            )}
                        </Typography>
                        <Typography
                            sx={{
                                px: 2,
                                pt: 0.5,
                                pb: 1.5,
                                fontSize: '0.875rem',
                                color: 'rgba(255,255,255,0.85)',
                                fontFamily: '"DM Sans", sans-serif',
                                lineHeight: 1.5,
                            }}
                        >
                            {
                                'En Podscope medimos programas, redes sociales y presencia de marcas en contenido digital.'
                            }
                        </Typography>
                    </Box>

                    <Box sx={{ px: 2, pb: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleOpenContact}
                            sx={{
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                color: '#FFFFFF',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.875rem',
                                borderRadius: '8px',
                                py: 1,
                                border: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            Quiero tener acceso
                        </Button>
                    </Box>
                </Box>
            )}

            <ContactDialog
                open={contactOpen}
                onClose={handleContactClose}
                initialMessage="Hola, vi el Ranking Podscope y quiero saber más sobre Podscope"
            />
        </>
    );
};

const METRIC_COLUMN_IDS = [
    'contentCount',
    'totalViews',
    'viewsPerEp',
    'totalInteractions',
    'interactionsPerEp',
] as const;

type MetricColumnId = (typeof METRIC_COLUMN_IDS)[number];

/** Mobile: sortable metrics first so they sit in the default viewport before horizontal scroll. */
const PROGRAMS_METRIC_ORDER_MOBILE: MetricColumnId[] = [
    'viewsPerEp',
    'interactionsPerEp',
    'contentCount',
    'totalViews',
    'totalInteractions',
];

const CHANNELS_METRIC_ORDER_MOBILE: MetricColumnId[] = [
    'totalViews',
    'totalInteractions',
    'contentCount',
    'viewsPerEp',
    'interactionsPerEp',
];

/** Desktop / larger: count, then totals, then averages. */
const PROGRAMS_METRIC_ORDER_DESKTOP: MetricColumnId[] = [
    'contentCount',
    'totalViews',
    'viewsPerEp',
    'totalInteractions',
    'interactionsPerEp',
];

const CHANNELS_METRIC_ORDER_DESKTOP: MetricColumnId[] = [
    'contentCount',
    'totalViews',
    'viewsPerEp',
    'totalInteractions',
    'interactionsPerEp',
];

const METRIC_COLUMN_WIDTH: Record<MetricColumnId, string> = {
    contentCount: '97px',
    totalViews: '87px',
    viewsPerEp: '87px',
    totalInteractions: '83px',
    interactionsPerEp: '87px',
};

const RANK_COL_WIDTH_PX = 44;
const CHEVRON_COL_WIDTH_PX = 44;
/** Desktop name track — room for avatar + readable title. */
const NAME_COL_MIN_DESKTOP_PX = 169;
/** Mobile stacked image/name/podcast column (≈ half the previous 218px floor). */
const NAME_COL_MIN_MOBILE_PX = 109;
/** Narrowest sort col that still fits compact numbers on small phones. */
const COMPACT_SORT_COL_MIN_PX = 64;
/** Preferred width of each default-visible sortable col on mobile. */
const COMPACT_SORT_COL_BASE_PX = 87;
/** Fallback scrollport when ResizeObserver has not measured yet (~iPhone SE). */
const MOBILE_SCROLLPORT_FALLBACK_PX = 360;

type MobileViewportCols = {
    nameWidth: number;
    sortColWidth: number;
};

/**
 * Mobile: fit # | stacked name | 2 sort metrics | chevron in the first viewport.
 * Name column is intentionally narrow (stacked image/name/podcast).
 */
function mobileViewportColWidths(scrollportWidth: number): MobileViewportCols {
    const width = scrollportWidth > 0 ? scrollportWidth : MOBILE_SCROLLPORT_FALLBACK_PX;
    const fixedChrome = RANK_COL_WIDTH_PX + CHEVRON_COL_WIDTH_PX;
    const available = Math.max(0, width - fixedChrome);

    let sortColWidth = Math.min(
        COMPACT_SORT_COL_BASE_PX,
        Math.max(COMPACT_SORT_COL_MIN_PX, Math.floor(available * 0.28))
    );
    let nameWidth = available - sortColWidth * 2;

    if (nameWidth < NAME_COL_MIN_MOBILE_PX) {
        nameWidth = Math.min(NAME_COL_MIN_MOBILE_PX, Math.max(96, Math.floor(available * 0.36)));
        sortColWidth = Math.max(COMPACT_SORT_COL_MIN_PX, Math.floor((available - nameWidth) / 2));
    }

    const visibleTotal = fixedChrome + nameWidth + sortColWidth * 2;
    if (visibleTotal > width) {
        nameWidth = Math.max(96, width - fixedChrome - sortColWidth * 2);
    }

    return { nameWidth, sortColWidth };
}

function metricColumnWidthPx(
    columnId: MetricColumnId,
    compactFirstSortables: boolean,
    firstSortableIds: ReadonlySet<MetricColumnId>,
    sortColWidth: number
): number {
    if (compactFirstSortables && firstSortableIds.has(columnId)) {
        return sortColWidth;
    }
    return parseInt(METRIC_COLUMN_WIDTH[columnId], 10);
}

/** # | name | metrics… | expand */
function buildRankingGridColumns(
    metricOrder: MetricColumnId[],
    compactMobile: boolean,
    scrollportWidth: number
): string {
    const firstSortableIds = new Set(metricOrder.slice(0, 2));
    const { nameWidth, sortColWidth } = mobileViewportColWidths(scrollportWidth);
    const nameCol = compactMobile ? `${nameWidth}px` : `minmax(${NAME_COL_MIN_DESKTOP_PX}px, 1fr)`;
    const metrics = metricOrder
        .map(id => `${metricColumnWidthPx(id, compactMobile, firstSortableIds, sortColWidth)}px`)
        .join(' ');
    return `${RANK_COL_WIDTH_PX}px ${nameCol} ${metrics} ${CHEVRON_COL_WIDTH_PX}px`;
}

function rankingTableMinWidth(
    metricOrder: MetricColumnId[],
    compactMobile: boolean,
    scrollportWidth: number
): number {
    const firstSortableIds = new Set(metricOrder.slice(0, 2));
    const { nameWidth, sortColWidth } = mobileViewportColWidths(scrollportWidth);
    const resolvedName = compactMobile ? nameWidth : NAME_COL_MIN_DESKTOP_PX;
    const metricsWidth = metricOrder.reduce(
        (sum, id) => sum + metricColumnWidthPx(id, compactMobile, firstSortableIds, sortColWidth),
        0
    );
    return RANK_COL_WIDTH_PX + resolvedName + metricsWidth + CHEVRON_COL_WIDTH_PX;
}

const STICKY_CELL_BG = '#121016'; // explorerDarkTheme background.paper
/** Opaque paper + 8% white — matches `action.hover` over paper without see-through. */
const STICKY_CELL_HOVER_BG = '#252329';

const stickyRankSx = {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    bgcolor: STICKY_CELL_BG,
    backgroundColor: STICKY_CELL_BG,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
} as const;

const stickyNameSx = {
    position: 'sticky',
    left: RANK_COL_WIDTH_PX,
    zIndex: 3,
    bgcolor: STICKY_CELL_BG,
    backgroundColor: STICKY_CELL_BG,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    // Soft edge so scrolled metrics don’t peek under the name cell
    boxShadow: `4px 0 8px -2px ${STICKY_CELL_BG}`,
} as const;

/** Program/channel name: up to 2 lines, then ellipsis. */
const RANKING_NAME_TEXT_SX = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: 1.25,
    minWidth: 0,
    width: '100%',
} as const;

const stickyChevronSx = {
    position: 'sticky',
    right: 0,
    zIndex: 3,
    bgcolor: STICKY_CELL_BG,
    backgroundColor: STICKY_CELL_BG,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: `-4px 0 8px -2px ${STICKY_CELL_BG}`,
} as const;

const RANKING_HEADER_TYPOGRAPHY_SX = {
    color: 'text.secondary',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
} as const;

/** Per-cell padding — must match content rows (do not put px on the grid container). */
const RANKING_HEADER_CELL_SX = {
    px: 1,
    py: 1.25,
    minWidth: 0,
    boxSizing: 'border-box',
    ...RANKING_HEADER_TYPOGRAPHY_SX,
} as const;

const RANKING_METRIC_HEADER_SX = {
    ...RANKING_HEADER_CELL_SX,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'visible',
} as const;

const RANKING_METRIC_CELL_SX = {
    px: 1,
    py: 1.5,
    minWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    alignSelf: 'stretch',
    width: '100%',
    whiteSpace: 'nowrap',
} as const;

const VIDEO_VIEWS_TODAY_HEADER = 'Views hoy';

function formatViewsHoyTooltip(updatedAt: string | null): string {
    if (!updatedAt) return 'Actualizado hoy';
    const date = new Date(updatedAt);
    if (Number.isNaN(date.getTime())) return 'Actualizado hoy';

    const timeParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: 'America/Santiago',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const hour = timeParts.find(p => p.type === 'hour')?.value ?? '';
    const minute = timeParts.find(p => p.type === 'minute')?.value ?? '';
    if (!hour || !minute) return 'Actualizado hoy';

    return `Actualizado a las ${hour}:${minute}`;
}

function getMetricColumnMeta(
    columnId: MetricColumnId,
    isProgramsMode: boolean
): { label: string; tooltip: string; sortField: PublicExplorerSortField | null } {
    switch (columnId) {
        case 'contentCount':
            return isProgramsMode
                ? {
                      label: 'Videos',
                      tooltip: 'Número de videos publicados en el período',
                      sortField: null,
                  }
                : {
                      label: 'Programas',
                      tooltip: 'Programas con videos publicados en el período',
                      sortField: null,
                  };
        case 'totalViews':
            return {
                label: 'views',
                tooltip: 'Total de visualizaciones en el período',
                sortField: 'totalViews',
            };
        case 'viewsPerEp':
            return {
                label: 'views prom.',
                tooltip: 'Promedio de views por video',
                sortField: 'viewsPerEp',
            };
        case 'totalInteractions':
            return {
                label: 'int.',
                tooltip: 'Total de likes + comentarios',
                sortField: 'totalInteractions',
            };
        case 'interactionsPerEp':
            return {
                label: 'int. prom.',
                tooltip: 'Promedio de interacciones por video',
                sortField: 'interactionsPerEp',
            };
    }
}

function getMetricCellValue(
    columnId: MetricColumnId,
    row: PublicExplorerProgramRow | PublicExplorerChannelRow,
    isProgramsMode: boolean
): string | number {
    switch (columnId) {
        case 'contentCount':
            return isProgramsMode
                ? row.contentCount
                : ((row as PublicExplorerChannelRow).programCount ?? row.contentCount);
        case 'totalViews':
            return formatNumber(row.totalViews);
        case 'viewsPerEp':
            return formatNumber(row.viewsPerEp);
        case 'totalInteractions':
            return formatNumber(row.totalInteractions);
        case 'interactionsPerEp':
            return formatNumber(row.interactionsPerEp);
    }
}

function RowVideosPanel({
    mode,
    rowId,
    queryParams,
    scrollportWidth,
    compact,
    totalContentCount,
}: {
    mode: PublicExplorerRankingMode;
    rowId: string;
    queryParams: PublicExplorerProgramsParams;
    scrollportWidth: number;
    compact: boolean;
    /** Period episode count from the ranking row (for “y N videos más”). */
    totalContentCount: number;
}) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['public-explorer-row-videos-v9', mode, rowId, queryParams],
        queryFn: () =>
            mode === 'programs'
                ? getPublicExplorerProgramVideos(rowId, queryParams)
                : getPublicExplorerChannelVideos(rowId, queryParams),
        staleTime: 5 * 60 * 1000,
    });

    const entityLabel = mode === 'programs' ? 'programa' : 'canal';
    // Backend sorts by day-7 views and caps (3 in prod, more in local).
    const previewVideos = data?.videos ?? [];
    const extraVideoCount = Math.max(0, totalContentCount - previewVideos.length);

    const panelWidth = scrollportWidth > 0 ? scrollportWidth : '100%';

    return (
        <Box
            sx={{
                position: 'sticky',
                left: 0,
                width: panelWidth,
                maxWidth: panelWidth,
                boxSizing: 'border-box',
                py: 1.5,
                bgcolor: SURFACE_MUTED,
                zIndex: 1,
                overflowX: 'hidden',
                minWidth: 0,
                // Keep gestures vertical so the parent table doesn't pan sideways here
                touchAction: 'pan-y',
                overscrollBehaviorX: 'none',
            }}
        >
            {isLoading ? (
                <Stack spacing={1} sx={{ px: 1.5, minWidth: 0, width: '100%' }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} height={36} />
                    ))}
                </Stack>
            ) : isError ? (
                <Alert severity="error" sx={{ py: 0, mx: 1.5 }}>
                    No se pudieron cargar los videos
                </Alert>
            ) : data?.videos.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1.5 }}>
                    Sin videos en este {entityLabel} para el período
                </Typography>
            ) : compact ? (
                <Stack
                    spacing={0}
                    sx={{ px: 1.5, minWidth: 0, width: '100%', boxSizing: 'border-box' }}
                >
                    {previewVideos.map((video, idx) => {
                        const ytUrl = getYouTubeUrl(video);
                        return (
                            <Box
                                key={`${video.title}-${idx}`}
                                sx={{
                                    py: 1.25,
                                    minWidth: 0,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    borderBottom:
                                        idx < previewVideos.length - 1 || extraVideoCount > 0
                                            ? 1
                                            : 0,
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    title={video.title}
                                    sx={{
                                        mb: 0.75,
                                        minWidth: 0,
                                        maxWidth: '100%',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {video.title}
                                </Typography>
                                <Stack spacing={0.35} sx={{ minWidth: 0, width: '100%', mb: 0.75 }}>
                                    <Typography variant="caption" color="text.primary">
                                        Views día 7: {formatNumber(video.viewsAtDay7)}
                                    </Typography>
                                    <Tooltip
                                        title={formatViewsHoyTooltip(video.viewsNowUpdatedAt)}
                                        arrow
                                        placement="top"
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Views hoy: {formatNumber(video.viewsNow)}
                                        </Typography>
                                    </Tooltip>
                                </Stack>
                                {ytUrl ? (
                                    <Link
                                        href={ytUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={e => e.stopPropagation()}
                                        sx={{
                                            display: 'inline-block',
                                            color: 'text.secondary',
                                            fontSize: '0.8125rem',
                                            textDecorationColor: 'rgba(255,255,255,0.35)',
                                            '&:hover': { color: 'text.primary' },
                                        }}
                                    >
                                        Ver en YouTube
                                    </Link>
                                ) : (
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        component="span"
                                    >
                                        —
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                    {extraVideoCount > 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 0.75 }}>
                            y {extraVideoCount} {extraVideoCount === 1 ? 'video más' : 'videos más'}
                        </Typography>
                    ) : null}
                </Stack>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        px: 1.5,
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                'minmax(0, 1fr) minmax(0, 100px) minmax(0, 88px) minmax(0, 120px)',
                            alignItems: 'center',
                            columnGap: 1,
                            py: 0.75,
                            borderBottom: 1,
                            borderColor: 'divider',
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Video
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.primary"
                            fontWeight={600}
                            sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                            Views día 7
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                            {VIDEO_VIEWS_TODAY_HEADER}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textAlign: 'right' }}
                        >
                            Link
                        </Typography>
                    </Box>
                    <Stack spacing={0}>
                        {previewVideos.map((video, idx) => {
                            const ytUrl = getYouTubeUrl(video);
                            const totalDisplayRows =
                                previewVideos.length + (extraVideoCount > 0 ? 1 : 0);
                            return (
                                <Box
                                    key={`${video.title}-${idx}`}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'minmax(0, 1fr) minmax(0, 100px) minmax(0, 88px) minmax(0, 120px)',
                                        alignItems: 'center',
                                        columnGap: 1,
                                        width: '100%',
                                        minWidth: 0,
                                        py: 0.75,
                                        borderBottom: idx < totalDisplayRows - 1 ? 1 : 0,
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        noWrap
                                        title={video.title}
                                        sx={{ minWidth: 0 }}
                                    >
                                        {video.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{ textAlign: 'right' }}
                                    >
                                        {formatNumber(video.viewsAtDay7)}
                                    </Typography>
                                    <Tooltip
                                        title={formatViewsHoyTooltip(video.viewsNowUpdatedAt)}
                                        arrow
                                        placement="top"
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ textAlign: 'right' }}
                                        >
                                            {formatNumber(video.viewsNow)}
                                        </Typography>
                                    </Tooltip>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        {ytUrl ? (
                                            <Link
                                                href={ytUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={e => e.stopPropagation()}
                                                sx={{
                                                    color: 'text.secondary',
                                                    textDecorationColor: 'rgba(255,255,255,0.35)',
                                                    '&:hover': { color: 'text.primary' },
                                                }}
                                            >
                                                Ver en YouTube
                                            </Link>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.disabled"
                                                component="span"
                                            >
                                                —
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                        {extraVideoCount > 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ py: 0.75, minWidth: 0 }}
                            >
                                y {extraVideoCount}{' '}
                                {extraVideoCount === 1 ? 'video más' : 'videos más'}
                            </Typography>
                        ) : null}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

const RANKING_MODE_LABELS: Record<PublicExplorerRankingMode, string> = {
    programs: 'Programas',
    channels: 'Canales',
};

/** URL hash fragments for deep-linking program vs channel view (`/ranking#programas`). */
const RANKING_MODE_HASH: Record<PublicExplorerRankingMode, string> = {
    programs: 'programas',
    channels: 'canales',
};

function rankingModeFromHash(hash: string): PublicExplorerRankingMode | null {
    const key = hash.replace(/^#/, '').toLowerCase();
    if (key === RANKING_MODE_HASH.programs) return 'programs';
    if (key === RANKING_MODE_HASH.channels) return 'channels';
    return null;
}

function hashForRankingMode(mode: PublicExplorerRankingMode): string {
    return `#${RANKING_MODE_HASH[mode]}`;
}

function initialRankingMode(): PublicExplorerRankingMode {
    if (typeof window === 'undefined') return 'programs';
    return rankingModeFromHash(window.location.hash) ?? 'programs';
}

/** Stable Select width — fits "Programas" label + chevron. */
const MODE_SELECT_WIDTH_XS = 132;
const MODE_SELECT_WIDTH_SM = 132;

const MODE_CHIP_HEIGHT_PX = 36;
const modeHeaderChipTypography = {
    color: 'white',
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '0.82rem',
    fontWeight: 600,
} as const;
const modeHeaderChipShape = {
    height: `${MODE_CHIP_HEIGHT_PX}px`,
    borderRadius: '20px',
} as const;
const modeHeaderChipFill = {
    backgroundColor: 'rgba(255,255,255,0.14)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.12)',
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
} as const;
const modeHeaderChipBorderedSurface = {
    ...modeHeaderChipShape,
    ...modeHeaderChipFill,
    border: '1px solid rgba(255,255,255,0.5)',
} as const;
const modeHeaderChipHover = {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderColor: 'rgba(255,255,255,0.75)',
} as const;
const modeHeaderChipFocus = {
    backgroundColor: 'rgba(255,255,255,0.22)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.28), 0 0 0 2px rgba(255,255,255,0.35)',
    borderColor: 'rgba(255,255,255,0.9)',
} as const;
const modeHeaderChipDisabled = {
    color: 'rgba(255,255,255,0.45)',
    borderColor: 'rgba(255,255,255,0.28)',
    backgroundColor: 'rgba(255,255,255,0.06)',
} as const;

/** Compact share chip — matches /live Compartir control. */
const shareChipButtonSx = {
    minWidth: 0,
    px: { xs: 0.625, sm: 1 },
    py: 0,
    height: `${MODE_CHIP_HEIGHT_PX}px`,
    borderRadius: 0,
    textTransform: 'none' as const,
    ...modeHeaderChipTypography,
    fontSize: { xs: '0.72rem', sm: '0.8rem' },
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& .MuiButton-startIcon': {
        marginRight: '4px',
        marginLeft: '-2px',
    },
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
        boxShadow: 'none',
    },
    '&.Mui-focusVisible': {
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        color: modeHeaderChipDisabled.color,
    },
};

const RANKINGS_SHARE_URL = 'https://podscope.co/ranking';

const RIGHT_SORT_HEADER_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0.5,
    width: '100%',
} as const;

type SortColumn = PublicExplorerSortField;

function ProgramAvatar({
    name,
    imageS3Key,
    fallbackImageS3Key,
    size = 40,
}: {
    name: string;
    imageS3Key: string | null;
    fallbackImageS3Key?: string | null;
    size?: number;
}) {
    const imageKey = imageS3Key || fallbackImageS3Key || null;
    const src = imageKey ? getS3Url(imageKey) : '';
    if (src) {
        return (
            <Box
                component="img"
                src={src}
                alt={name}
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 1,
                    objectFit: 'cover',
                    flexShrink: 0,
                    bgcolor: SURFACE_MUTED,
                }}
            />
        );
    }
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: 1,
                bgcolor: SURFACE_MUTED,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: size <= 28 ? '0.75rem' : undefined,
                color: 'text.secondary',
                flexShrink: 0,
            }}
        >
            {name.charAt(0).toUpperCase()}
        </Box>
    );
}

const PublicExplorerRankings: React.FC = () => {
    const { accessStatusPending, gatedNoAccess, canFetchContent, accessUrlFlash } =
        useLiveRankingsAccessGate();
    const theme = useTheme();
    const isMobileModeLabel = useMediaQuery(theme.breakpoints.down('sm'));
    const isCompactDetails = useMediaQuery(theme.breakpoints.down('md'));
    const prefersReducedMotion = useReducedMotion();
    const scrollportRef = useRef<HTMLDivElement | null>(null);
    const [scrollportWidth, setScrollportWidth] = useState(0);
    const [rankingMode, setRankingMode] = useState<PublicExplorerRankingMode>(initialRankingMode);
    const [sortColumn, setSortColumn] = useState<SortColumn>(() =>
        getPublicExplorerDefaultSortForMode(initialRankingMode())
    );
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [contactOpen, setContactOpen] = useState(false);

    const handleShare = useCallback(async () => {
        if (gatedNoAccess) return;
        const shareUrl = `${RANKINGS_SHARE_URL}${hashForRankingMode(rankingMode)}`;
        const shareData = {
            title: 'Ranking Podscope',
            text: 'Ranking de programas y canales de YouTube en Chile · Métricas a 7 días de estreno',
            url: shareUrl,
        };
        try {
            if (typeof navigator.share === 'function') {
                await navigator.share(shareData);
                return;
            }
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            }
        } catch (err) {
            const name = err && typeof err === 'object' && 'name' in err ? String(err.name) : '';
            if (name !== 'AbortError') console.error('Share failed:', err);
        }
    }, [gatedNoAccess, rankingMode]);

    const effectiveSort = isPublicExplorerSortAllowedForMode(rankingMode, sortColumn)
        ? sortColumn
        : getPublicExplorerDefaultSortForMode(rankingMode);

    const programsQueryParams = useMemo(
        (): PublicExplorerProgramsParams => ({
            sortBy:
                rankingMode === 'programs'
                    ? effectiveSort
                    : getPublicExplorerDefaultSortForMode('programs'),
        }),
        [rankingMode, effectiveSort]
    );

    const channelsQueryParams = useMemo(
        (): PublicExplorerProgramsParams => ({
            sortBy:
                rankingMode === 'channels'
                    ? effectiveSort
                    : getPublicExplorerDefaultSortForMode('channels'),
        }),
        [rankingMode, effectiveSort]
    );

    const queryParams = rankingMode === 'channels' ? channelsQueryParams : programsQueryParams;

    const metricOrder = useMemo(() => {
        if (isMobileModeLabel) {
            return rankingMode === 'channels'
                ? CHANNELS_METRIC_ORDER_MOBILE
                : PROGRAMS_METRIC_ORDER_MOBILE;
        }
        return rankingMode === 'channels'
            ? CHANNELS_METRIC_ORDER_DESKTOP
            : PROGRAMS_METRIC_ORDER_DESKTOP;
    }, [isMobileModeLabel, rankingMode]);
    const firstSortableIds = useMemo(() => {
        const sorts =
            rankingMode === 'channels'
                ? (['totalViews', 'totalInteractions'] as MetricColumnId[])
                : (['viewsPerEp', 'interactionsPerEp'] as MetricColumnId[]);
        return new Set(sorts);
    }, [rankingMode]);
    const gridColumns = useMemo(
        () => buildRankingGridColumns(metricOrder, isMobileModeLabel, scrollportWidth),
        [metricOrder, isMobileModeLabel, scrollportWidth]
    );
    const tableMinWidth = useMemo(
        () => rankingTableMinWidth(metricOrder, isMobileModeLabel, scrollportWidth),
        [metricOrder, isMobileModeLabel, scrollportWidth]
    );
    const rankingGridSx = useMemo(
        () =>
            ({
                display: 'grid',
                gridTemplateColumns: gridColumns,
                alignItems: 'stretch',
                width: '100%',
                minWidth: tableMinWidth,
            }) as const,
        [gridColumns, tableMinWidth]
    );

    useLayoutEffect(() => {
        const el = scrollportRef.current;
        if (!el) return;
        const update = () => setScrollportWidth(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [tableMinWidth, metricOrder, isMobileModeLabel]);

    const programsQuery = useQuery({
        queryKey: ['public-explorer-programs-v6', programsQueryParams],
        queryFn: () => getPublicExplorerPrograms(programsQueryParams),
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
        enabled: canFetchContent,
    });

    const channelsQuery = useQuery({
        queryKey: ['public-explorer-channels-v7', channelsQueryParams],
        queryFn: () => getPublicExplorerChannels(channelsQueryParams),
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
        enabled: canFetchContent,
    });

    const activeQuery = rankingMode === 'programs' ? programsQuery : channelsQuery;
    const { data, isLoading, isFetching, isError, error } = activeQuery;

    useEffect(() => {
        setExpandedRowId(null);
    }, [rankingMode, queryParams]);

    useEffect(() => {
        if (gatedNoAccess) return;
        analytics.track(AnalyticsEvent.VIEW_PUBLIC_EXPLORER_RANKINGS);
    }, [gatedNoAccess]);

    const handleOpenContact = useCallback(() => {
        analytics.track(AnalyticsEvent.CLICK_REQUEST_EXPLORER, {
            location: 'public_rankings',
        });
        setContactOpen(true);
    }, []);

    const handleRankingModeChange = useCallback((mode: PublicExplorerRankingMode) => {
        setRankingMode(mode);
        setSortColumn(getPublicExplorerDefaultSortForMode(mode));
        setExpandedRowId(null);
        const nextHash = hashForRankingMode(mode);
        if (window.location.hash !== nextHash) {
            window.location.hash = nextHash;
        }
    }, []);

    useEffect(() => {
        const onHashChange = () => {
            const mode = rankingModeFromHash(window.location.hash);
            if (mode) handleRankingModeChange(mode);
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, [handleRankingModeChange]);

    const handleSortHeaderClick = useCallback(
        (column: SortColumn) => {
            if (isPublicExplorerSortAllowedForMode(rankingMode, column)) {
                setSortColumn(column);
                return;
            }
            handleOpenContact();
        },
        [rankingMode, handleOpenContact]
    );

    const showRefetching = isFetching && !isLoading;
    const isProgramsMode = rankingMode === 'programs';
    const entityLabelPlural = isProgramsMode ? 'programas' : 'canales';
    const nameColumnLabel = isProgramsMode ? 'Programa' : 'Canal';
    const remainingCount = Math.max(0, (data?.totalCount ?? 0) - (data?.rows.length ?? 0));
    const totalCount = data?.totalCount ?? 0;
    const showExplorerMoreNote =
        canFetchContent &&
        !isLoading &&
        !isError &&
        (data?.rows.length ?? 0) > 0 &&
        remainingCount > 0;

    const gatedPeriodLabel = 'Período disponible al ingresar';

    if (accessStatusPending) {
        return (
            <PageContainer>
                <ThemeProvider theme={explorerDarkTheme}>
                    <Helmet>
                        <title>Radar Podscope | Podscope</title>
                    </Helmet>
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            minHeight: '60vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress sx={{ color: 'white' }} />
                    </Box>
                </ThemeProvider>
            </PageContainer>
        );
    }

    const getRowId = (row: PublicExplorerProgramRow | PublicExplorerChannelRow): string =>
        isProgramsMode
            ? (row as PublicExplorerProgramRow).programId
            : (row as PublicExplorerChannelRow).channelId;

    const getRowSubtitle = (
        row: PublicExplorerProgramRow | PublicExplorerChannelRow
    ): string | null => (isProgramsMode ? (row as PublicExplorerProgramRow).channelTitle : null);

    const renderMetricHeader = (label: string, tooltip: string) => (
        <Tooltip title={tooltip} arrow placement="top">
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Box>
        </Tooltip>
    );

    const renderSortLabel = (column: SortColumn, label: string, tooltip: string) => {
        const isAllowed = isPublicExplorerSortAllowedForMode(rankingMode, column);
        const isActive = effectiveSort === column;
        const stackPromLabel = column === 'viewsPerEp' || column === 'interactionsPerEp';
        return (
            <Tooltip title={tooltip} arrow placement="top">
                <Box
                    component="button"
                    type="button"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSortHeaderClick(column);
                    }}
                    sx={{
                        ...RIGHT_SORT_HEADER_SX,
                        m: 0,
                        p: 0,
                        border: 0,
                        background: 'none',
                        cursor: 'pointer',
                        color: isActive ? '#FFFFFF' : 'text.secondary',
                        font: 'inherit',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        opacity: isAllowed || isActive ? 1 : 0.85,
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        textAlign: 'right',
                        whiteSpace: stackPromLabel ? 'normal' : 'nowrap',
                        // Only clip on stacked mobile headers; desktop labels must stay fully visible
                        ...(stackPromLabel ? { overflow: 'hidden' } : { overflow: 'visible' }),
                        '&:hover': { color: '#FFFFFF' },
                    }}
                >
                    {stackPromLabel ? (
                        <Box
                            component="span"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.25,
                                width: '100%',
                                minWidth: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    minWidth: 0,
                                    flex: '1 1 auto',
                                    lineHeight: 1.15,
                                    textAlign: 'right',
                                    whiteSpace: 'normal',
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{ display: 'block', width: '100%', textAlign: 'right' }}
                                >
                                    {column === 'viewsPerEp' ? 'views' : 'int.'}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{ display: 'block', width: '100%', textAlign: 'right' }}
                                >
                                    prom.
                                </Box>
                            </Box>
                            {isActive ? (
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        flexShrink: 0,
                                        fontSize: '0.7rem',
                                        lineHeight: 1,
                                        opacity: 0.9,
                                        alignSelf: 'center',
                                    }}
                                >
                                    ↓
                                </Box>
                            ) : null}
                        </Box>
                    ) : (
                        <>
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                                {label}
                            </Box>
                            {isActive ? (
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        fontSize: '0.7rem',
                                        lineHeight: 1,
                                        opacity: 0.9,
                                    }}
                                >
                                    ↓
                                </Box>
                            ) : null}
                        </>
                    )}
                </Box>
            </Tooltip>
        );
    };

    const renderMetricHeaderCell = (columnId: MetricColumnId) => {
        const meta = getMetricColumnMeta(columnId, isProgramsMode);
        const stackPromHeader = columnId === 'viewsPerEp' || columnId === 'interactionsPerEp';
        const isDefaultVisibleSort = isMobileModeLabel && firstSortableIds.has(columnId);
        return (
            <Box
                key={columnId}
                sx={{
                    ...RANKING_METRIC_HEADER_SX,
                    justifyContent: 'flex-end',
                    textAlign: 'right',
                    ...(isDefaultVisibleSort ? { px: 0.75, minWidth: 0, overflow: 'hidden' } : {}),
                    ...(stackPromHeader
                        ? {
                              whiteSpace: 'normal',
                              alignItems: 'center',
                          }
                        : {}),
                }}
            >
                {meta.sortField
                    ? renderSortLabel(meta.sortField, meta.label, meta.tooltip)
                    : renderMetricHeader(meta.label, meta.tooltip)}
            </Box>
        );
    };

    return (
        <PageContainer>
            <ThemeProvider theme={explorerDarkTheme}>
                <Helmet>
                    <title>Radar Podscope | Podscope</title>
                    <meta
                        name="description"
                        content="Ranking de programas y canales de YouTube en Chile con métricas a 7 días de estreno."
                    />
                    <link rel="canonical" href="https://podscope.co/ranking" />
                </Helmet>

                <Box sx={liveRankingsPageOuterSx}>
                    <Container
                        maxWidth={LIVE_RANKINGS_PAGE_MAX_WIDTH}
                        sx={liveRankingsPageContainerSx}
                    >
                        <Box
                            sx={{
                                mb: gatedNoAccess ? '16px' : '32px',
                                textAlign: 'center',
                                position: 'relative',
                                width: '100%',
                                overflow: 'visible',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    width: '100%',
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    columnGap: 1,
                                    gridTemplateColumns: '1fr auto',
                                    mb: 0,
                                    overflow: 'visible',
                                    visibility: gatedNoAccess ? 'hidden' : 'visible',
                                    height: gatedNoAccess ? 0 : 'auto',
                                    ...(gatedNoAccess ? { overflow: 'hidden' } : {}),
                                }}
                            >
                                <Box
                                    sx={{
                                        justifySelf: 'start',
                                        alignSelf: { xs: 'flex-start', sm: 'center' },
                                        width: {
                                            xs: `min(${MODE_SELECT_WIDTH_XS}px, calc(100% - 48px))`,
                                            sm: `${MODE_SELECT_WIDTH_SM}px`,
                                        },
                                        minWidth: {
                                            xs: `min(${MODE_SELECT_WIDTH_XS}px, calc(100% - 48px))`,
                                            sm: `${MODE_SELECT_WIDTH_SM}px`,
                                        },
                                        maxWidth: {
                                            xs: 'calc(100% - 48px)',
                                            sm: 'none',
                                        },
                                        zIndex: 2,
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                        overflow: 'visible',
                                        ...modeHeaderChipBorderedSurface,
                                        '&:hover': {
                                            ...modeHeaderChipHover,
                                            boxShadow: modeHeaderChipFill.boxShadow,
                                        },
                                        '&:focus-within': {
                                            ...modeHeaderChipFocus,
                                        },
                                    }}
                                >
                                    <motion.div
                                        initial={{ scale: 1 }}
                                        animate={
                                            prefersReducedMotion || isMobileModeLabel
                                                ? { scale: 1 }
                                                : { scale: [1, 1.04, 1, 1.04, 1, 1.025, 1] }
                                        }
                                        transition={
                                            prefersReducedMotion || isMobileModeLabel
                                                ? undefined
                                                : {
                                                      duration: 3,
                                                      ease: 'easeInOut',
                                                      times: [0, 0.14, 0.28, 0.45, 0.6, 0.8, 1],
                                                  }
                                        }
                                        style={{
                                            display: 'flex',
                                            width: '100%',
                                            height: '100%',
                                            transformOrigin: 'left center',
                                        }}
                                    >
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            sx={{
                                                m: 0,
                                                mt: 0,
                                                mb: 0,
                                                height: '100%',
                                                minHeight: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Select<PublicExplorerRankingMode>
                                                value={rankingMode}
                                                onChange={e =>
                                                    handleRankingModeChange(
                                                        e.target.value as PublicExplorerRankingMode
                                                    )
                                                }
                                                displayEmpty
                                                renderValue={value => RANKING_MODE_LABELS[value]}
                                                inputProps={{
                                                    'aria-label': 'Agrupar ranking por',
                                                }}
                                                MenuProps={{
                                                    disableScrollLock: true,
                                                    anchorOrigin: {
                                                        vertical: 'bottom',
                                                        horizontal: 'center',
                                                    },
                                                    transformOrigin: {
                                                        vertical: 'top',
                                                        horizontal: 'center',
                                                    },
                                                    PaperProps: {
                                                        sx: {
                                                            width: MODE_SELECT_WIDTH_XS,
                                                            maxWidth: 'calc(100vw - 24px)',
                                                            minWidth: 0,
                                                            overflowX: 'hidden',
                                                            bgcolor: 'rgba(33, 35, 45, 0.98)',
                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                            borderRadius: '12px',
                                                            mt: 0.5,
                                                            '& .MuiMenuItem-root': {
                                                                color: 'rgba(255,255,255,0.9)',
                                                                fontFamily: '"DM Sans", sans-serif',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 500,
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            },
                                                            '& .MuiMenuItem-root.Mui-selected': {
                                                                bgcolor: 'rgba(255,255,255,0.12)',
                                                            },
                                                            '& .MuiMenuItem-root:hover': {
                                                                bgcolor: 'rgba(255,255,255,0.08)',
                                                            },
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    minWidth: 0,
                                                    minHeight: 0,
                                                    ...modeHeaderChipTypography,
                                                    borderRadius: modeHeaderChipShape.borderRadius,
                                                    backgroundColor: 'transparent !important',
                                                    boxShadow: 'none',
                                                    // Select root IS MuiOutlinedInput-root (theme sets paper bg)
                                                    '&.MuiOutlinedInput-root': {
                                                        width: '100%',
                                                        height: `${MODE_CHIP_HEIGHT_PX}px`,
                                                        minHeight: `${MODE_CHIP_HEIGHT_PX}px`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        overflow: 'hidden',
                                                        backgroundColor: 'transparent !important',
                                                        boxShadow: 'none',
                                                        borderRadius:
                                                            modeHeaderChipShape.borderRadius,
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'transparent !important',
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor:
                                                                'transparent !important',
                                                            boxShadow: 'none',
                                                        },
                                                    },
                                                    '& .MuiSelect-select': {
                                                        backgroundColor: 'transparent !important',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        boxSizing: 'border-box',
                                                        pr: '32px !important',
                                                        pl: '12px !important',
                                                        py: '0 !important',
                                                        minHeight: '0 !important',
                                                        height: `${MODE_CHIP_HEIGHT_PX}px`,
                                                        lineHeight: 1.2,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none !important',
                                                    },
                                                    '& .MuiSvgIcon-root': { color: 'white' },
                                                }}
                                            >
                                                <MenuItem value="programs">Programas</MenuItem>
                                                <MenuItem value="channels">Canales</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </motion.div>
                                </Box>

                                <Box
                                    sx={{
                                        justifySelf: 'end',
                                        alignSelf: { xs: 'flex-start', sm: 'center' },
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'stretch',
                                            ...modeHeaderChipBorderedSurface,
                                            '&:hover': {
                                                ...modeHeaderChipHover,
                                                boxShadow: modeHeaderChipFill.boxShadow,
                                            },
                                            '&:focus-within': {
                                                ...modeHeaderChipFocus,
                                            },
                                        }}
                                    >
                                        <Button
                                            type="button"
                                            variant="text"
                                            disableElevation
                                            onClick={() => {
                                                void handleShare();
                                            }}
                                            disabled={gatedNoAccess}
                                            aria-label="Compartir ranking"
                                            startIcon={<IosShareIcon sx={{ fontSize: '1rem' }} />}
                                            sx={shareChipButtonSx}
                                        >
                                            Compartir
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 4, mb: gatedNoAccess ? 1.5 : 3, width: '100%' }}>
                                <HeaderBrandTitle
                                    component="p"
                                    sx={{ m: 0, mb: 0.75, width: '100%' }}
                                >
                                    Radar Podscope
                                </HeaderBrandTitle>
                                <HeaderTitle variant="h1" sx={{ m: 0, width: '100%' }}>
                                    {isProgramsMode ? 'Programas' : 'Canales'} x Métricas a 7 días
                                </HeaderTitle>
                            </Box>

                            {gatedNoAccess ? (
                                <WeekBadge
                                    sx={{
                                        maxWidth: { xs: 'min(100%, 340px)', sm: 520 },
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        lineHeight: 1.35,
                                        py: 1,
                                        px: 1.5,
                                        fontSize: { xs: '0.78rem', sm: '0.9rem' },
                                    }}
                                >
                                    {gatedPeriodLabel}
                                </WeekBadge>
                            ) : isLoading ? (
                                <Skeleton
                                    variant="rounded"
                                    animation="wave"
                                    sx={{
                                        display: 'inline-block',
                                        verticalAlign: 'middle',
                                        height: { xs: '30px', sm: '34px' },
                                        width: { xs: 'min(260px, 72vw)', sm: '280px' },
                                        borderRadius: '20px',
                                        bgcolor: 'rgba(255,255,255,0.12)',
                                    }}
                                />
                            ) : (
                                <WeekBadge
                                    sx={{
                                        maxWidth: { xs: 'min(100%, 340px)', sm: 520 },
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        lineHeight: 1.35,
                                        py: 1,
                                        px: 1.5,
                                        fontSize: { xs: '0.78rem', sm: '0.9rem' },
                                    }}
                                >
                                    {data?.periodLabel ?? ''}
                                </WeekBadge>
                            )}
                        </Box>

                        {gatedNoAccess ? (
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    mt: 1,
                                }}
                            >
                                <LiveRankingsGate
                                    accessError={accessUrlFlash}
                                    title="Radar Podscope"
                                />
                            </Box>
                        ) : null}

                        {!gatedNoAccess && isError ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {(error as Error)?.message || 'Error al cargar el ranking'}
                            </Alert>
                        ) : null}

                        {!gatedNoAccess ? (
                            <>
                                {isMobileModeLabel ? (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                        spacing={0.5}
                                        sx={{ mb: 1, px: 0.25, minHeight: 20 }}
                                        aria-hidden
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ letterSpacing: 0.02 }}
                                        >
                                            Desliza para ver más
                                        </Typography>
                                        <ChevronRightIcon
                                            sx={{
                                                fontSize: 16,
                                                color: 'text.secondary',
                                            }}
                                        />
                                    </Stack>
                                ) : null}
                                <Paper
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        ...(showRefetching
                                            ? { opacity: 0.55, pointerEvents: 'none' }
                                            : {}),
                                    }}
                                >
                                    {showRefetching ? (
                                        <LinearProgress
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                zIndex: 1,
                                            }}
                                        />
                                    ) : null}

                                    <Box
                                        ref={scrollportRef}
                                        sx={{
                                            overflowX: 'auto',
                                            WebkitOverflowScrolling: 'touch',
                                            overscrollBehaviorX: 'contain',
                                            scrollbarWidth: 'thin',
                                        }}
                                    >
                                        <Box sx={{ minWidth: tableMinWidth }}>
                                            <Box
                                                sx={{
                                                    ...rankingGridSx,
                                                    ...RANKING_HEADER_TYPOGRAPHY_SX,
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        ...RANKING_HEADER_CELL_SX,
                                                        ...stickyRankSx,
                                                        zIndex: 3,
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    #
                                                </Box>
                                                <Box
                                                    sx={{
                                                        ...RANKING_HEADER_CELL_SX,
                                                        ...stickyNameSx,
                                                        zIndex: 3,
                                                    }}
                                                >
                                                    {nameColumnLabel}
                                                </Box>
                                                {metricOrder.map(renderMetricHeaderCell)}
                                                <Box sx={{ ...stickyChevronSx, zIndex: 3 }} />
                                            </Box>

                                            {isLoading
                                                ? Array.from({ length: 10 }).map((_, i) => (
                                                      <Box
                                                          key={`sk-${i}`}
                                                          sx={{
                                                              px: 1.5,
                                                              py: 1.5,
                                                              borderBottom: 1,
                                                              borderColor: 'divider',
                                                          }}
                                                      >
                                                          <Skeleton height={48} />
                                                      </Box>
                                                  ))
                                                : data?.rows.map(row => {
                                                      const rowId = getRowId(row);
                                                      const isExpanded = expandedRowId === rowId;
                                                      const subtitle = getRowSubtitle(row);
                                                      return (
                                                          <React.Fragment key={rowId}>
                                                              <Box
                                                                  onClick={() =>
                                                                      setExpandedRowId(prev =>
                                                                          prev === rowId
                                                                              ? null
                                                                              : rowId
                                                                      )
                                                                  }
                                                                  sx={{
                                                                      ...rankingGridSx,
                                                                      borderBottom: 1,
                                                                      borderColor: 'divider',
                                                                      cursor: 'pointer',
                                                                      bgcolor: STICKY_CELL_BG,
                                                                      '&:hover': {
                                                                          bgcolor:
                                                                              STICKY_CELL_HOVER_BG,
                                                                      },
                                                                      '&:hover [data-sticky-cell]':
                                                                          {
                                                                              bgcolor:
                                                                                  STICKY_CELL_HOVER_BG,
                                                                              backgroundColor:
                                                                                  STICKY_CELL_HOVER_BG,
                                                                              boxShadow: 'none',
                                                                          },
                                                                      '&:hover [data-sticky-name]':
                                                                          {
                                                                              boxShadow: `4px 0 8px -2px ${STICKY_CELL_HOVER_BG}`,
                                                                          },
                                                                      '&:hover [data-sticky-chevron]':
                                                                          {
                                                                              boxShadow: `-4px 0 8px -2px ${STICKY_CELL_HOVER_BG}`,
                                                                          },
                                                                  }}
                                                              >
                                                                  <Box
                                                                      data-sticky-cell
                                                                      sx={{
                                                                          ...stickyRankSx,
                                                                          justifyContent: 'center',
                                                                          px: 1.5,
                                                                          py: 1.5,
                                                                      }}
                                                                  >
                                                                      <Typography
                                                                          variant="body2"
                                                                          fontWeight={600}
                                                                          color="text.secondary"
                                                                      >
                                                                          {row.rank}
                                                                      </Typography>
                                                                  </Box>
                                                                  <Box
                                                                      data-sticky-cell
                                                                      data-sticky-name
                                                                      sx={{
                                                                          ...stickyNameSx,
                                                                          px: isMobileModeLabel
                                                                              ? 0.75
                                                                              : 1.5,
                                                                          py: isMobileModeLabel
                                                                              ? 1
                                                                              : 1.5,
                                                                          width: '100%',
                                                                      }}
                                                                  >
                                                                      <Stack
                                                                          direction={
                                                                              isMobileModeLabel
                                                                                  ? 'column'
                                                                                  : 'row'
                                                                          }
                                                                          spacing={
                                                                              isMobileModeLabel
                                                                                  ? 0.5
                                                                                  : 1.5
                                                                          }
                                                                          alignItems={
                                                                              isMobileModeLabel
                                                                                  ? 'flex-start'
                                                                                  : 'center'
                                                                          }
                                                                          sx={{
                                                                              minWidth: 0,
                                                                              width: '100%',
                                                                              overflow: 'hidden',
                                                                          }}
                                                                      >
                                                                          <ProgramAvatar
                                                                              name={row.name}
                                                                              imageS3Key={
                                                                                  row.imageS3Key
                                                                              }
                                                                              fallbackImageS3Key={
                                                                                  isProgramsMode
                                                                                      ? (
                                                                                            row as PublicExplorerProgramRow
                                                                                        )
                                                                                            .channelImageS3Key
                                                                                      : null
                                                                              }
                                                                              size={
                                                                                  isMobileModeLabel
                                                                                      ? 36
                                                                                      : 40
                                                                              }
                                                                          />
                                                                          <Box
                                                                              sx={{
                                                                                  minWidth: 0,
                                                                                  flex: 1,
                                                                                  overflow:
                                                                                      'hidden',
                                                                                  width: '100%',
                                                                              }}
                                                                          >
                                                                              <Typography
                                                                                  variant={
                                                                                      isMobileModeLabel
                                                                                          ? 'body2'
                                                                                          : 'subtitle1'
                                                                                  }
                                                                                  fontWeight={600}
                                                                                  title={row.name}
                                                                                  sx={{
                                                                                      ...RANKING_NAME_TEXT_SX,
                                                                                      ...(isMobileModeLabel
                                                                                          ? {
                                                                                                WebkitLineClamp: 2,
                                                                                                fontSize:
                                                                                                    '0.8125rem',
                                                                                            }
                                                                                          : {}),
                                                                                  }}
                                                                              >
                                                                                  {row.name}
                                                                              </Typography>
                                                                              {subtitle ? (
                                                                                  <Typography
                                                                                      variant="caption"
                                                                                      color="text.secondary"
                                                                                      title={
                                                                                          subtitle
                                                                                      }
                                                                                      sx={{
                                                                                          ...RANKING_NAME_TEXT_SX,
                                                                                          WebkitLineClamp:
                                                                                              isMobileModeLabel
                                                                                                  ? 1
                                                                                                  : 2,
                                                                                          ...(isMobileModeLabel
                                                                                              ? {
                                                                                                    fontSize:
                                                                                                        '0.6875rem',
                                                                                                    mt: 0.15,
                                                                                                }
                                                                                              : {}),
                                                                                      }}
                                                                                  >
                                                                                      {subtitle}
                                                                                  </Typography>
                                                                              ) : null}
                                                                          </Box>
                                                                      </Stack>
                                                                  </Box>
                                                                  {metricOrder.map(columnId => {
                                                                      const compactSortCell =
                                                                          isMobileModeLabel &&
                                                                          firstSortableIds.has(
                                                                              columnId
                                                                          );
                                                                      return (
                                                                          <Typography
                                                                              key={columnId}
                                                                              variant="body2"
                                                                              sx={{
                                                                                  ...RANKING_METRIC_CELL_SX,
                                                                                  textAlign:
                                                                                      'right',
                                                                                  justifyContent:
                                                                                      'flex-end',
                                                                                  ...(compactSortCell
                                                                                      ? { px: 0.75 }
                                                                                      : {}),
                                                                              }}
                                                                          >
                                                                              {getMetricCellValue(
                                                                                  columnId,
                                                                                  row,
                                                                                  isProgramsMode
                                                                              )}
                                                                          </Typography>
                                                                      );
                                                                  })}
                                                                  <Box
                                                                      data-sticky-cell
                                                                      data-sticky-chevron=""
                                                                      sx={{
                                                                          ...stickyChevronSx,
                                                                          py: 1.5,
                                                                      }}
                                                                  >
                                                                      <IconButton
                                                                          size="small"
                                                                          aria-label="expandir videos"
                                                                          sx={{
                                                                              p: 0.25,
                                                                              transform: isExpanded
                                                                                  ? 'rotate(180deg)'
                                                                                  : 'rotate(0deg)',
                                                                              transition:
                                                                                  'transform 0.2s',
                                                                          }}
                                                                      >
                                                                          <ExpandMoreIcon />
                                                                      </IconButton>
                                                                  </Box>
                                                              </Box>
                                                              {isExpanded ? (
                                                                  <Box
                                                                      onClick={e =>
                                                                          e.stopPropagation()
                                                                      }
                                                                      sx={{
                                                                          borderBottom: 1,
                                                                          borderColor: 'divider',
                                                                          width:
                                                                              scrollportWidth > 0
                                                                                  ? scrollportWidth
                                                                                  : '100%',
                                                                          maxWidth:
                                                                              scrollportWidth > 0
                                                                                  ? scrollportWidth
                                                                                  : '100%',
                                                                          minWidth: 0,
                                                                          overflowX: 'hidden',
                                                                          position: 'sticky',
                                                                          left: 0,
                                                                          touchAction: 'pan-y',
                                                                          overscrollBehaviorX:
                                                                              'none',
                                                                      }}
                                                                  >
                                                                      <RowVideosPanel
                                                                          key={rowId}
                                                                          mode={rankingMode}
                                                                          rowId={rowId}
                                                                          queryParams={queryParams}
                                                                          scrollportWidth={
                                                                              scrollportWidth
                                                                          }
                                                                          compact={isCompactDetails}
                                                                          totalContentCount={
                                                                              row.contentCount ?? 0
                                                                          }
                                                                      />
                                                                  </Box>
                                                              ) : null}
                                                          </React.Fragment>
                                                      );
                                                  })}

                                            {!isLoading && data?.rows.length === 0 ? (
                                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                                    <Typography color="text.secondary">
                                                        No hay {entityLabelPlural} para este período
                                                    </Typography>
                                                </Box>
                                            ) : null}
                                        </Box>
                                    </Box>
                                </Paper>

                                {showExplorerMoreNote ? (
                                    <Card
                                        sx={{
                                            mt: 1.5,
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                p: { xs: 2, sm: 2.5 },
                                                '&:last-child': { pb: { xs: 2, sm: 2.5 } },
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                gap: { xs: 2, sm: 3 },
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        color: '#F7F7F7',
                                                        fontFamily: '"DM Sans", sans-serif',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                                                        lineHeight: 1.35,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    Datos completos en el Explorador
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(247, 247, 247, 0.72)',
                                                        fontSize: {
                                                            xs: '0.8125rem',
                                                            sm: '0.875rem',
                                                        },
                                                        lineHeight: 1.45,
                                                    }}
                                                >
                                                    Incluye estos y{' '}
                                                    {remainingCount.toLocaleString('es-CL')}{' '}
                                                    {entityLabelPlural} activos adicionales del
                                                    período.
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    analytics.track(
                                                        AnalyticsEvent.CLICK_REQUEST_EXPLORER,
                                                        {
                                                            location: 'public_rankings_more_note',
                                                            mode: rankingMode,
                                                            remainingCount,
                                                            totalCount,
                                                        }
                                                    );
                                                    setContactOpen(true);
                                                }}
                                                sx={{
                                                    flexShrink: 0,
                                                    alignSelf: { xs: 'stretch', sm: 'center' },
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: 2.75,
                                                    py: 1.1,
                                                    color: '#FFFFFF',
                                                    background:
                                                        'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        background:
                                                            'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                                                        boxShadow: 'none',
                                                    },
                                                }}
                                            >
                                                Quiero tener acceso
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ) : null}

                                <Card
                                    sx={{
                                        mt: 3.5,
                                        mb: 2,
                                        bgcolor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                color: '#F7F7F7',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                mb: 0.75,
                                            }}
                                        >
                                            ¿Por qué métricas a 7 días?
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                display: 'block',
                                                color: 'rgba(247, 247, 247, 0.82)',
                                                fontSize: '0.8125rem',
                                                lineHeight: 1.55,
                                            }}
                                        >
                                            Para los rankings en Podscope medimos views e
                                            interacciones siete días después de la publicación de
                                            cada episodio. Así comparamos contenidos estrenados en
                                            fechas distintas en igualdad de condiciones, sin
                                            favorecer a videos más antiguos solo porque llevaron más
                                            tiempo online.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </>
                        ) : null}
                    </Container>
                </Box>

                <ContactDialog
                    open={contactOpen}
                    onClose={() => setContactOpen(false)}
                    title="Solicitar acceso al explorador"
                    subtitle="Déjanos tus datos y te contactaremos para darte acceso al ranking completo del Explorador."
                    initialMessage={`Me interesa acceder al Explorador de ${isProgramsMode ? 'Programas' : 'Canales'} de Podscope.`}
                />

                {!gatedNoAccess && (
                    <ExplorerRankingsPopup
                        programCount={programsQuery.data?.totalCount ?? 0}
                        channelCount={channelsQuery.data?.totalCount ?? 0}
                    />
                )}
            </ThemeProvider>
        </PageContainer>
    );
};

export default PublicExplorerRankings;
